import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Loader2, Box, XCircle, ArrowRight, Unlock } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_LABELS = {
  early_adopter: "Early Adopter",
  standard: "Standard",
};

const UpgradeSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`);
        setPaymentDetails(response.data);

        if (response.data.payment_status === "paid") {
          setStatus("success");
        } else {
          setTimeout(pollPaymentStatus, 2000);
        }
      } catch (error) {
        console.error("Failed to check payment status:", error);
        setStatus("error");
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  const tierLabel = paymentDetails?.tier
    ? TIER_LABELS[paymentDetails.tier] || paymentDetails.tier.replace(/_/g, " ")
    : "Premium";

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4" data-testid="upgrade-success-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md text-center"
      >
        <div className="flex items-center justify-center gap-2 mb-8">
          <Box className="w-8 h-8 text-blue-500" />
          <span className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
            ParamSnap
          </span>
        </div>

        <div className="bento-card p-8">
          {status === "loading" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-8"
              data-testid="payment-processing"
            >
              <Loader2 className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
                Confirming Payment...
              </h2>
              <p className="text-sm text-zinc-400">
                Please wait while we verify your payment and unlock your account.
              </p>
            </motion.div>
          )}

          {status === "success" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-8"
              data-testid="payment-success"
            >
              <div className="relative inline-block mb-4">
                <CheckCircle className="w-16 h-16 text-green-500" />
                <Unlock className="w-6 h-6 text-green-400 absolute -bottom-1 -right-1 bg-[#121214] rounded-full p-0.5" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
                Account Unlocked
              </h2>
              <p className="text-sm text-zinc-400 mb-6">
                You're now on the <span className="text-green-400 font-semibold">{tierLabel}</span> plan.
                Unlimited part generations are active immediately — open Fusion 360 and start building.
              </p>

              {paymentDetails && (
                <div className="bg-white/5 rounded-lg p-4 mb-6 text-left space-y-2" data-testid="payment-details">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Plan</span>
                    <span className="text-green-400 font-medium">{tierLabel}</span>
                  </div>
                  {paymentDetails.amount_total > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Amount paid</span>
                      <span className="text-white font-medium">
                        ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                      </span>
                    </div>
                  )}
                  {paymentDetails.email && (
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Account</span>
                      <span className="text-white font-medium">{paymentDetails.email}</span>
                    </div>
                  )}
                </div>
              )}

              <Button
                onClick={() => navigate("/")}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="back-to-home-btn"
              >
                Back to ParamSnap
                <ArrowRight className="w-4 h-4" />
              </Button>
            </motion.div>
          )}

          {status === "error" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-8"
              data-testid="payment-error"
            >
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "Chivo, sans-serif" }}>
                Something Went Wrong
              </h2>
              <p className="text-sm text-zinc-400 mb-6">
                We couldn't verify your payment. If you were charged, please contact support and we'll sort it out.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/")}
                  className="btn-primary w-full"
                  data-testid="error-back-btn"
                >
                  Back to ParamSnap
                </Button>
                <a
                  href="mailto:support@paramsnap.com"
                  className="block text-sm text-blue-400 hover:text-blue-300"
                  data-testid="contact-support-link"
                >
                  Contact Support
                </a>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default UpgradeSuccess;
